// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * ui.ts — DOM builders for the surfaces that are not the canvas: the hand, the
 * help and about sheets, and the results screen.
 */

import { type GameState, pressureOn, ringNeeded, type Seat } from './game';
import { PIECE_ORDER, type PieceType } from './modes';
import { SEAT_NAME } from './palette';

export const PIECE_INFO: Record<PieceType, { glyph: string; name: string; note: string }> = {
  heartroot: {
    glyph: '✷',
    name: 'Heartroot',
    note: 'Never moves. Ring it to win. Always your first placement.',
  },
  creeper: {
    glyph: 'C',
    name: 'Creeper',
    note: 'Walks the rim, keeping contact. 1 step nested, 3 reaching.',
  },
  burrower: {
    glyph: 'B',
    name: 'Burrower',
    note: 'Squeezes through gaps nothing else fits. 1 nested, up to 2 reaching.',
  },
  spindle: {
    glyph: 'S',
    name: 'Spindle',
    note: 'Leaps a straight line of pieces. One piece nested, any line reaching.',
  },
  warden: {
    glyph: 'W',
    name: 'Warden',
    note: 'Pins every enemy piece it touches. 1 step nested, 2 reaching.',
  },
  sporeling: {
    glyph: 'o',
    name: 'Sporeling',
    note: 'Placed only, never moves — and converts one enemy piece it lands beside.',
  },
};

export function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );
}

/** The hand tray: one tile per piece type, with the count remaining. */
export function renderHand(
  el: HTMLElement,
  game: GameState,
  seat: Seat | null,
  held: PieceType | null,
  canAct: boolean,
  onPick: (p: PieceType) => void,
): void {
  el.innerHTML = '';
  if (seat === null) return;
  const hand = game.hands[seat];
  const mustPlaceRoot = hand.heartroot > 0;

  for (const type of PIECE_ORDER) {
    const n = hand[type];
    if (n <= 0 && type !== 'heartroot') continue;
    if (n <= 0) continue;
    const info = PIECE_INFO[type];
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `hand-tile s${seat}`;
    btn.setAttribute('aria-pressed', String(held === type));
    btn.disabled = !canAct || (mustPlaceRoot && type !== 'heartroot');
    btn.setAttribute(
      'aria-label',
      `${info.name}, ${n} in hand. ${info.note}`,
    );
    btn.innerHTML = `
      <span class="hand-glyph">${escapeHtml(info.glyph)}</span>
      <span class="hand-label">${escapeHtml(info.name)}</span>
      <span class="hand-count">×${n}</span>`;
    btn.addEventListener('click', () => onPick(type));
    el.appendChild(btn);
  }
}

export function helpSheetHtml(): string {
  const rows = PIECE_ORDER.map(
    (p) =>
      `<dt>${escapeHtml(PIECE_INFO[p].glyph)}</dt><dd><strong>${escapeHtml(
        PIECE_INFO[p].name,
      )}</strong> — ${escapeHtml(PIECE_INFO[p].note)}</dd>`,
  ).join('');

  return `
    <div class="sheet" role="dialog" aria-modal="true" aria-labelledby="help-title">
      <h2 id="help-title">How to play</h2>
      <p>There is no board — just pieces, and they all touch. Each turn you either
        <strong>place</strong> a piece from your hand or <strong>move</strong> one already out there.</p>

      <h3>The one rule everything hangs on</h3>
      <p>Every piece, yours and theirs, forms <strong>one single colony</strong> — and it must stay
        one. You may never place or move in a way that would split it in two.</p>

      <h3>How you win</h3>
      <p>Fill every cell around your rival's <strong>Heartroot</strong>. Pieces of
        <em>either</em> colour count, so walling your own Heartroot in is how you lose.</p>

      <h3>Polarity — the twist</h3>
      <p>A piece surrounded by <strong>your own</strong> colour is <em>nested</em>: safe, and it
        moves exactly one cell. A piece outnumbered by the <strong>enemy</strong> is
        <em>reaching</em>: exposed, and it moves its full range. Safety is slow. Overextending is
        fast. Select a piece and the halo tells you which it is.</p>

      <h3>The wither</h3>
      <p>As the colony ages a Heartroot chokes on fewer cells — the collar around each one shows
        exactly how many it needs now. An early lead can't be banked, and a late position always
        resolves.</p>

      <h3>The pieces</h3>
      <dl class="piece-key">${rows}</dl>

      <p class="muted">Tap a piece or a hand tile to select it, then tap a highlighted cell. You can
        also drag. On desktop, click the same way.</p>
      <div class="sheet-actions">
        <button class="btn primary" data-close>Got it</button>
      </div>
    </div>`;
}

export function aboutSheetHtml(): string {
  return `
    <div class="sheet" role="dialog" aria-modal="true" aria-labelledby="about-title">
      <h2 id="about-title">About Rootbound</h2>
      <p>A boardless hex duel: grow one shared colony, and ring your rival's Heartroot before they
        ring yours. Original rules — the polarity mechanic, the Sporeling's conversion and the
        withering ring are Rootbound's own.</p>

      <h3>Privacy</h3>
      <p>No accounts, no cookies, no tracking, no third-party fonts. The only analytics are
        anonymous, cookie-less page-view counts via Cloudflare Web Analytics.</p>

      <h3>Multiplayer</h3>
      <p>Games with a friend are <strong>peer-to-peer</strong> — the two browsers talk directly and
        there is no game server. A public signaling relay is used only to introduce the two peers to
        each other; no game state is ever stored on it. Rootbound sends nothing but your moves, and
        rooms are private to whoever has the code.</p>

      <h3>Balance</h3>
      <p class="muted">Every mode was tuned by simulating hundreds of AI-vs-AI games rather than by
        guessing. Seat fairness, draw rate and how early a lead decides the game are all measured,
        and the findings — including the ones that proved the designer wrong — are in
        <code>BALANCE.md</code> in the repo.</p>

      <div class="sheet-actions">
        <button class="btn primary" data-close>Close</button>
      </div>
    </div>`;
}

export interface ResultRow {
  seat: Seat;
  name: string;
  isSelf: boolean;
}

/**
 * The results screen shows EVERY player's outcome, not just the local one
 * (principle #9) — with a per-player breakdown of what each actually did, so
 * the one moment players compare themselves is not just you reflected back at
 * yourself.
 */
export function resultsHtml(game: GameState, rows: ResultRow[], tally: [number, number]): string {
  const verdictFor = (self: ResultRow | undefined): { text: string; cls: string } => {
    if (game.winner === null) return { text: 'Deadlock', cls: '' };
    if (!self) return { text: `${SEAT_NAME[game.winner]} wins`, cls: '' };
    return game.winner === self.seat
      ? { text: 'You win', cls: 'win' }
      : { text: 'You lose', cls: 'loss' };
  };
  const self = rows.find((r) => r.isSelf);
  const v = verdictFor(self);

  const why = {
    ring: 'The ring closed.',
    pressure: 'Time ran out — decided on pressure.',
    stalemate: 'Neither colony could move.',
    resign: 'By resignation.',
  }[game.reason ?? 'ring'];

  const body = rows
    .map((r) => {
      const need = ringNeeded(game.mode, game.ply, r.seat);
      const onThem = pressureOn(game, r.seat === 0 ? 1 : 0);
      const onMe = pressureOn(game, r.seat);
      const placed =
        [...game.cells.values()].filter((p) => p.owner === r.seat).length;
      const won = game.winner === r.seat;
      return `<tr class="${r.isSelf ? 'self' : ''}">
        <td><span class="name-cell"><span class="seat-dot s${r.seat}"></span>${escapeHtml(
          r.name,
        )}${won ? ' 🏆' : ''}</span></td>
        <td class="num">${onThem}/${ringNeeded(game.mode, game.ply, r.seat === 0 ? 1 : 0)}</td>
        <td class="num">${onMe}/${need}</td>
        <td class="num">${placed}</td>
      </tr>`;
    })
    .join('');

  return `
    <div class="result-head">
      <p class="result-verdict ${v.cls}">${escapeHtml(v.text)}</p>
      <p class="result-why">${escapeHtml(why)} ${game.ply} moves.</p>
    </div>
    <table class="scoreboard">
      <thead>
        <tr>
          <th>Player</th>
          <th class="num" title="Cells filled around the OPPONENT's Heartroot">Squeeze</th>
          <th class="num" title="Cells filled around this player's OWN Heartroot">Choked</th>
          <th class="num">Pieces</th>
        </tr>
      </thead>
      <tbody>${body}</tbody>
    </table>
    ${
      tally[0] + tally[1] > 0
        ? `<p class="tally">Match: ${SEAT_NAME[0]} ${tally[0]} — ${tally[1]} ${SEAT_NAME[1]}</p>`
        : ''
    }`;
}
